import { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import AssetCard, { AssetRow } from "@/components/AssetCard";
import AssetDetailModal from "@/components/AssetDetailModal";
import UploadModal from "@/components/UploadModal";
import ShareModal from "@/components/ShareModal";
import BulkActionBar from "@/components/BulkActionBar";
import api, { API } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { Film, Tags as TagsIcon, Link2, FileText, CheckSquare, Square, ChevronRight, Folder, Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";

const BANNER_DEFAULT = "https://images.unsplash.com/photo-1632187981988-40f3cbaeef5e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function FolderBreadcrumbs({ folders, folderId }) {
  const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
  const chain = [];
  let cur = byId[folderId];
  while (cur) {
    chain.unshift(cur);
    cur = cur.parent_id ? byId[cur.parent_id] : null;
  }
  if (chain.length <= 1) {
    return <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-1">Folder</div>;
  }
  return (
    <nav
      data-testid="folder-breadcrumbs"
      className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-1 flex items-center gap-1 flex-wrap"
    >
      <span>Folder</span>
      <ChevronRight className="w-2.5 h-2.5 opacity-60" />
      {chain.slice(0, -1).map((f, i) => (
        <span key={f.id} className="flex items-center gap-1">
          <Link to={`/folders/${f.id}`} className="hover:text-[var(--brand-accent)] transition truncate max-w-[140px]">{f.name}</Link>
          <ChevronRight className="w-2.5 h-2.5 opacity-60" />
        </span>
      ))}
      <span className="text-[var(--brand-accent)] truncate max-w-[180px]" title={chain[chain.length - 1].name}>{chain[chain.length - 1].name}</span>
    </nav>
  );
}

export default function Dashboard() {
  const { folderId } = useParams();
  const { user } = useAuth();
  const canUpload = ["admin", "editor"].includes(user?.role);
  // Iter 72b — clients can one-click a view-only link on any folder in
  // their scope. They never see "Share folder" (full modal) or
  // "Feedback report" (admin-only PDF).
  // Iter 73 — The "Copy share link" button now appears ONLY at the scope
  // root. Admin/editor: top-level folders (parent_id === null). Client:
  // their client_scope_folder_id. Drilling into any subfolder hides the
  // row — one share link per project, period.
  const canShareFolder = canUpload || user?.role === "client";

  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("wd_view") || "grid");
  const [search, setSearch] = useState("");
  const [activeAsset, setActiveAsset] = useState(null);
  // Deep-link support: /folders/:fid?asset=<id> opens that asset's modal.
  const _loc = useLocation();
  const _navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(_loc.search);
    const aid = params.get("asset");
    if (aid) setActiveAsset(aid);
  }, [_loc.search]);
  const [showUpload, setShowUpload] = useState(false);
  const [showShareFolder, setShowShareFolder] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  // Bulk selection
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [anchorId, setAnchorId] = useState(null);
  const toggleSelect = (id, event) => {
    // Shift+Click range-select: select all items between anchor and target
    if (event?.shiftKey && anchorId && anchorId !== id) {
      const ids = assets.map((a) => a.id);
      const i1 = ids.indexOf(anchorId);
      const i2 = ids.indexOf(id);
      if (i1 !== -1 && i2 !== -1) {
        const [lo, hi] = i1 < i2 ? [i1, i2] : [i2, i1];
        setSelected((prev) => {
          const next = new Set(prev);
          for (let i = lo; i <= hi; i++) next.add(ids[i]);
          return next;
        });
        setAnchorId(id);
        return;
      }
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setAnchorId(id);
  };
  const clearSelection = () => { setSelected(new Set()); setSelectMode(false); setAnchorId(null); };
  const selectAll = () => setSelected(new Set(assets.map((a) => a.id)));

  useEffect(() => { localStorage.setItem("wd_view", view); }, [view]);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get("/folders");
    setFolders(data);
  }, []);

  const loadAssets = useCallback(async () => {
    const params = {};
    if (folderId) params.folder_id = folderId;
    if (tagFilter) params.tag = tagFilter;
    const { data } = await api.get("/assets", { params });
    setAssets(data);
  }, [folderId, tagFilter]);

  const loadTags = useCallback(async () => {
    const params = folderId ? { folder_id: folderId } : {};
    const { data } = await api.get("/tags", { params });
    setAllTags(data);
  }, [folderId]);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadTags(); }, [loadTags]);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  // Auto-refresh assets if any are still processing
  useEffect(() => {
    const hasProcessing = assets.some((a) => ["uploading", "processing"].includes(a.status));
    if (!hasProcessing) return;
    const t = setInterval(loadAssets, 8000);
    return () => clearInterval(t);
  }, [assets, loadAssets]);

  const currentFolder = folders.find((f) => f.id === folderId);
  const banner = currentFolder?.banner_url || BANNER_DEFAULT;

  return (
    <div className="flex min-h-screen grain-overlay">
      {/* Iter 71 — Sonner mount for one-click share toast. */}
      <Toaster theme="dark" position="bottom-right" richColors />
      <Sidebar folders={folders} currentFolderId={folderId} onRefresh={loadFolders} />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar
          view={view}
          setView={setView}
          onSearch={setSearch}
          searchValue={search}
          onUpload={() => setShowUpload(true)}
          canUpload={canUpload && !!folderId}
          scope={currentFolder ? { folderId: currentFolder.id, folderName: currentFolder.name } : null}
        />

        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          {folderId && currentFolder ? (
            <>
              <div className="relative w-full h-48 md:h-64 rounded-xl overflow-hidden mb-6 md:mb-8">
                <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/50 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 sm:bottom-6 sm:left-6 sm:right-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
                  <div className="min-w-0">
                    <FolderBreadcrumbs folders={folders} folderId={currentFolder.id} />
                    <h1 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold tracking-tight break-words">{currentFolder.name}</h1>
                    <div className="text-xs sm:text-sm text-zinc-400 mt-1">{currentFolder.asset_count} {currentFolder.asset_count === 1 ? "video" : "videos"}</div>
                  </div>
                  {canShareFolder && (() => {
                    const isScopeRoot = user?.role === "client"
                      ? currentFolder.id === user?.client_scope_folder_id
                      : !currentFolder.parent_id;
                    // Iter 84 — Review Library: no Copy share link; Share folder on every level.
                    const isLibrary = currentFolder.category === "library";
                    if (!isScopeRoot && !(isLibrary && canUpload)) return null;
                    return (
                    <div className="shrink-0 flex items-center flex-wrap gap-2">
                      {canUpload && (
                        <>
                          {isScopeRoot && <button
                            data-testid={TID.reportFolderBtn}
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem("wd_token");
                                const res = await fetch(`${API}/folders/${currentFolder.id}/report.pdf`, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) throw new Error("Failed");
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `${currentFolder.name} - feedback report.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                              } catch (e) { alert("Could not generate report."); }
                            }}
                            className="flex items-center gap-2 bg-black/60 backdrop-blur-md border border-zinc-700/60 hover:border-[var(--brand-accent)]/60 text-zinc-100 hover:text-[var(--brand-accent)] rounded-full px-4 py-2 text-sm transition"
                            title="Download branded PDF of all client feedback for this folder"
                          >
                            <FileText className="w-3.5 h-3.5" /> Feedback report
                          </button>}
                          <button
                            data-testid={TID.shareFolderBtn}
                            onClick={() => setShowShareFolder(true)}
                            className="flex items-center gap-2 bg-black/60 backdrop-blur-md border border-zinc-700/60 hover:border-[var(--brand-accent)]/60 text-zinc-100 hover:text-[var(--brand-accent)] rounded-full px-4 py-2 text-sm transition"
                          >
                            <Link2 className="w-3.5 h-3.5" /> Share folder
                          </button>
                        </>
                      )}
                      {isScopeRoot && !isLibrary && <button
                        data-testid={TID.copyShareLinkBtn}
                        onClick={async () => {
                          try {
                            // Iter 80 — Idempotent quick-link endpoint.
                            // Returns an existing share for this folder
                            // if one exists; otherwise creates one. These
                            // quick-link shares are hidden from the
                            // Share Links admin area so the folder's
                            // "Copy share link" affordance stays
                            // noise-free.
                            const { data } = await api.post("/shares/folder-quick-link", {
                              kind: "folder",
                              target_id: currentFolder.id,
                              password: null,
                              expires_in_days: null,
                              allow_download: false,
                              allow_comments: false,
                              allow_approval: false,
                              include_subfolders: true,
                              title: `${currentFolder.name} — view only`,
                            });
                            await navigator.clipboard.writeText(data.url);
                            const slug = (data.url || "").split("/s/").pop();
                            toast.success(`Link copied — ready to paste · opens read-only · /s/${slug}`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Could not create share link");
                          }
                        }}
                        className="flex items-center gap-2 bg-black/60 backdrop-blur-md border border-zinc-700/60 hover:border-[var(--brand-accent)]/60 text-zinc-100 hover:text-[var(--brand-accent)] rounded-full px-4 py-2 text-sm transition"
                        title="One-click view-only link — copies to clipboard"
                      >
                        <Copy className="w-3.5 h-3.5" /> Copy share link
                      </button>}
                    </div>
                    );
                  })()}
                </div>
              </div>
            </>
          ) : (
            <div className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">All Videos</div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Library</h1>
              <p className="text-sm text-zinc-500 mt-1">{assets.length} {assets.length === 1 ? "video" : "videos"} across all folders</p>
            </div>
          )}

          {/* Subfolder tiles — filesystem-style drill-down for the current folder */}
          {folderId && (() => {
            const subfolders = folders.filter((f) => f.parent_id === folderId);
            if (subfolders.length === 0) return null;
            return (
              <section data-testid="subfolder-tiles" className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Folder className="w-3.5 h-3.5 text-zinc-500" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Subfolders</span>
                  <span className="text-[10px] text-zinc-600">({subfolders.length})</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                  {subfolders.map((sf) => (
                    <Link
                      key={sf.id}
                      to={`/folders/${sf.id}`}
                      data-testid={`subfolder-tile-${sf.id}`}
                      className="group bg-zinc-950/50 border border-zinc-800/60 rounded-lg p-3 hover:border-[var(--brand-accent)]/40 transition flex items-center gap-2"
                    >
                      <Folder className="w-4 h-4 text-[var(--brand-accent)] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-100 truncate group-hover:text-[var(--brand-accent)] transition">{sf.name}</div>
                        <div className="text-[10px] text-zinc-500">{sf.asset_count} {sf.asset_count === 1 ? "video" : "videos"}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })()}

          {/* Tag filter row + bulk-select toggle — folder view only.
              At the root ("All videos") we render zero videos in the main grid,
              so a filter bar has nothing to filter and would just add noise. */}
          {folderId && (allTags.length > 0 || (canUpload && assets.length > 0)) && (
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {allTags.length > 0 && (
                <>
                  <TagsIcon className="w-3.5 h-3.5 text-zinc-500" />
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-1">Filter:</span>
                  <button
                    onClick={() => setTagFilter(null)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition ${!tagFilter ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]" : "text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}
                  >
                    All
                  </button>
                  {allTags.slice(0, 15).map((t) => (
                    <button
                      key={t.name}
                      onClick={() => setTagFilter(t.name === tagFilter ? null : t.name)}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition ${tagFilter === t.name ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]" : "text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}
                    >
                      {t.name} <span className="opacity-60">{t.count}</span>
                    </button>
                  ))}
                </>
              )}
              {canUpload && assets.length > 0 && (
                <button
                  data-testid={TID.bulkSelectToggle}
                  onClick={() => { setSelectMode((p) => !p); setSelected(new Set()); setAnchorId(null); }}
                  title={selectMode ? "Tip: Shift+Click to select a range" : "Enter select mode"}
                  className={`ml-auto text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${selectMode ? "bg-[var(--brand-accent)] text-black border-[var(--brand-accent)]" : "text-zinc-400 border-zinc-800 hover:border-zinc-600"}`}
                >
                  {selectMode ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
                  {selectMode ? "Selecting" : "Select"}
                </button>
              )}
            </div>
          )}

          {assets.length === 0 ? (
            (() => {
              const hasSubs = folderId && folders.some((f) => f.parent_id === folderId);
              if (hasSubs) {
                return (
                  <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-900 rounded-xl">
                    <div className="text-sm">No videos directly in this folder. Open a subfolder above to see its videos.</div>
                  </div>
                );
              }
              return <EmptyState canUpload={canUpload && !!folderId} onUpload={() => setShowUpload(true)} hasFolder={!!folderId} />;
            })()
          ) : view === "grid" ? (
            <div
              data-testid={TID.assetGrid}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3"
            >
              {assets.map((a) => (
                <AssetCard
                  key={a.id}
                  asset={a}
                  onClick={() => setActiveAsset(a.id)}
                  selectMode={selectMode}
                  selected={selected.has(a.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
          ) : (
            <div data-testid={TID.assetGrid} className="border-t border-zinc-900/60">
              {assets.map((a) => (
                <AssetRow key={a.id} asset={a} onClick={() => setActiveAsset(a.id)} />
              ))}
            </div>
          )}
        </main>
      </div>

      {selectMode && selected.size > 0 && (
        <BulkActionBar
          selectedIds={Array.from(selected)}
          totalCount={assets.length}
          allFolders={folders}
          currentFolderId={folderId}
          onSelectAll={selectAll}
          onClear={clearSelection}
          onDone={() => { clearSelection(); loadAssets(); loadTags(); loadFolders(); }}
        />
      )}

      {activeAsset && (
        <AssetDetailModal
          assetId={activeAsset}
          onClose={() => {
            setActiveAsset(null);
            // Clear ?asset= via the router so location stays in sync.
            const p = new URLSearchParams(_loc.search);
            if (p.has("asset")) {
              p.delete("asset");
              const qs = p.toString();
              _navigate({ pathname: _loc.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
            }
          }}
          onChange={() => { loadAssets(); loadTags(); loadFolders(); }}
        />
      )}

      {showUpload && (
        <UploadModal
          folderId={folderId}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { loadAssets(); loadFolders(); }}
        />
      )}

      {showShareFolder && currentFolder && (
        <ShareModal
          target={{ kind: "folder", id: currentFolder.id, title: currentFolder.name }}
          onClose={() => setShowShareFolder(false)}
        />
      )}
    </div>
  );
}

function EmptyState({ canUpload, onUpload, hasFolder }) {
  return (
    <div data-testid={TID.emptyState} className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 rounded-full border border-zinc-800 flex items-center justify-center mb-4">
        <Film className="w-7 h-7 text-zinc-600" />
      </div>
      <h3 className="font-heading text-xl font-semibold tracking-tight text-zinc-300">
        {hasFolder ? "This folder is empty" : "Welcome to your library"}
      </h3>
      <p className="text-sm text-zinc-500 mt-1 max-w-md">
        {hasFolder
          ? "Upload your first video to start organizing and tagging."
          : "Select a folder in the sidebar or create one to start adding videos."}
      </p>
      {canUpload && (
        <button
          onClick={onUpload}
          className="mt-5 bg-[var(--brand-accent)] text-black font-semibold rounded-full px-5 py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition"
        >
          Upload videos
        </button>
      )}
    </div>
  );
}
